'use strict'

// PWA OOTB Hooks
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

import TrackOrderMgr from '../custom-api/track-order-custom-api'

/**
 * Custom hook to track an order using the TrackOrderMgr API.
 *
 * This hook provides a method to track orders by interacting with a custom API.
 * It handles the retrieval of an access token and manages the API call to track an order.
 *
 * @returns {Object} An object containing the `trackMyOrder` function.
 *
 * @property {Function} trackMyOrder - Asynchronous function to track an order.
 * @property {Object} trackMyOrder.params - Parameters required to track the order.
 * @property {Promise<Object>} trackMyOrder.return - A promise that resolves to an object with the tracking result.
 * @property {boolean} trackMyOrder.return.success - Indicates if the tracking was successful.
 * @property {string} trackMyOrder.return.message - Contains the success or error message from the tracking operation.
 *
 * @example
 * const { trackMyOrder } = useTrackMyOrder();
 * const result = await trackMyOrder({ orderId: '12345' });
 * console.log(result.success); // true or false
 * console.log(result.message); // Success or error message
 */
export const useTrackMyOrder = () => {
    // Create an instance of the Custom API class
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const api = new TrackOrderMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })

    return {
        /**
         * Tracks an order based on the provided parameters and returns the result.
         *
         * @async
         * @param {Object} params - The parameters required to track the order, typically including order ID and other relevant details.
         * @returns {Promise<Object>} A promise that resolves to an object containing the tracking result.
         * @property {boolean} success - Indicates whether the order tracking was successful.
         * @property {string} message - A message providing additional information about the tracking result or error.
         *
         * @throws {Error} If an error occurs during the tracking process, the error message will be included in the result.
         */
        async trackMyOrder(params) {
            let result = {
                success: false,
                message: '',
                order: null
            }

            try {
                const accessToken = await getTokenWhenReady()
                const trackOrderResult = await api.trackOrder(params, site.id, accessToken)
                if (trackOrderResult.success) {
                    result = {
                        success: trackOrderResult.success,
                        message: trackOrderResult.message,
                        order: trackOrderResult.order
                    }
                }
            } catch (error) {
                result.message = error.message
            }

            return result
        }
    }
}
